/**
 * Client-side vectorization of a template PNG.
 * Uses browser Canvas 2D – no server call, no memory issues.
 *
 * Algorithm:
 *  1. Downscale image to MAX_PX (long side) via canvas.
 *  2. Classify pixels: 0=black(material), 1=white(opening), 2=red(cut area).
 *  3. 4-connected BFS component labelling per class.
 *  4. Trace outer boundary of each component (Moore 8-connectivity).
 *  5. Douglas-Peucker simplification.
 *  6. Convert pixels → mm (origin = image centre).
 *
 * Trójkolorowe szablony (czerwone tło):
 *  Dwa przypadki cięcia szablonu:
 *  A) Czerwony wewnątrz (otoczony czarną ramką) → cutContours (normalny PIP)
 *  B) Czerwony sięga krawędzi obrazu → cutContoursInverted (odwrócony PIP:
 *     szukamy wysp czarno+białych w morzu czerwieni; trójkąty POZA wyspą = wycięte)
 *
 * Kluczowe decyzje projektowe w klasyfikacji (patrz classifyPixels):
 *  - Próg czerwonego: R≥128 && G<100 && B<100 (nie R≥150) – obejmuje blendy
 *    bilinearnego skalowania na granicy czerwono-czarnej.
 *  - Ramka B=6px: wymuszona czerwień przy brzegu obrazu eliminuje artefakty
 *    klasyfikacji (izolowana czarna ramka → osobny prostokątny kontur → gęsta siatka).
 */

import { VectorizeResult, Contour, ContourPoint } from '../types';

const MAX_PX = 1024;     // max image side for vectorization
const MIN_AREA_MM2 = 1;  // ignore components smaller than this

// ── Debug helpers (DEV ONLY) ──────────────────────────────────
// Ustaw DEBUG_VECTORIZE = false aby wyłączyć zapis PNG na serwer.
const DEBUG_VECTORIZE = true;

/**
 * Renderuje tablicę classified (0=czarny, 1=biały, 2=czerwony) na canvas,
 * konwertuje do PNG i wysyła do /api/debug/png (serwer zapisuje z timestampem).
 */
async function debugSaveClassified(
  classified: Uint8Array,
  w: number, h: number,
  name: string,
): Promise<void> {
  if (!DEBUG_VECTORIZE) return;
  try {
    const c   = document.createElement('canvas');
    c.width   = w; c.height = h;
    const ctx = c.getContext('2d')!;
    const img = ctx.createImageData(w, h);
    for (let i = 0; i < w * h; i++) {
      const cls = classified[i];
      // 0=czarny (materiał), 1=biały (otwór), 2=czerwony (wycięcie)
      const r = cls === 2 ? 255 : cls === 1 ? 255 : 0;
      const g = cls === 2 ? 0   : cls === 1 ? 255 : 0;
      const b = cls === 2 ? 0   : cls === 1 ? 255 : 0;
      img.data[i * 4]     = r;
      img.data[i * 4 + 1] = g;
      img.data[i * 4 + 2] = b;
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    const dataUrl = c.toDataURL('image/png');
    await fetch('/api/debug/png', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ dataUrl, name }),
    });
  } catch (e) {
    console.warn('[debugSaveClassified] Błąd:', e);
  }
}

/**
 * Renderuje maskę binarną (0/1) na canvas i wysyła do /api/debug/png.
 * Piksele = 1 → biały, 0 → czarny.
 */
async function debugSaveMask(
  mask: Uint8Array,
  w: number, h: number,
  name: string,
): Promise<void> {
  if (!DEBUG_VECTORIZE) return;
  try {
    const c   = document.createElement('canvas');
    c.width   = w; c.height = h;
    const ctx = c.getContext('2d')!;
    const img = ctx.createImageData(w, h);
    for (let i = 0; i < w * h; i++) {
      const v = mask[i] ? 255 : 0;
      img.data[i * 4]     = v;
      img.data[i * 4 + 1] = v;
      img.data[i * 4 + 2] = v;
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    const dataUrl = c.toDataURL('image/png');
    await fetch('/api/debug/png', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ dataUrl, name }),
    });
  } catch (e) {
    console.warn('[debugSaveMask] Błąd:', e);
  }
}

// ── Canvas helpers ────────────────────────────────────────────

function getPixels(
  dataUrl: string,
  maxPx: number,
): Promise<{ pixels: Uint8ClampedArray; w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, maxPx / Math.max(img.width, img.height));
      const w = Math.round(img.width  * s);
      const h = Math.round(img.height * s);
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d')!.drawImage(img, 0, 0, w, h);
      resolve({ pixels: c.getContext('2d')!.getImageData(0, 0, w, h).data, w, h });
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

// ── Pixel classification ──────────────────────────────────────
// 0 = black (background / materiał)
// 1 = white (otwór szablonu)
// 2 = red   (obszar do wycięcia z materiału)
//
// Progi:
//   Czerwony: R≥128 && G<100 && B<100 && A≥128
//   Biały:    R≥128 && A≥128  (i NIE spełnia warunku czerwonego)
//
// Dlaczego R≥128 (nie R≥150) dla czerwonego?
// Bilinearne skalowanie kanwasu tworzy piksele będące blendem czerwonego (255,0,0)
// i czarnego (0,0,0): np. R=140, G=5, B=5. Przy R≥150 te piksele wpadają
// w klasę białą (bo R≥128) zamiast czerwoną — powoduje to białe artefakty
// wzdłuż granicy czerwono-czarnej. R≥128 z warunkiem G<100 && B<100
// poprawnie klasyfikuje całe spektrum blendów czerwono-czarnych jako czerwone.

function classifyPixels(px: Uint8ClampedArray, n: number): Uint8Array {
  const b = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const r = px[i * 4], g = px[i * 4 + 1], bl = px[i * 4 + 2], a = px[i * 4 + 3];
    if (a < 128) { b[i] = 0; continue; }
    if (r >= 128 && g < 100 && bl < 100) { b[i] = 2; continue; }  // czerwony lub blend czerwono-czarny
    if (r >= 128) { b[i] = 1; continue; }
    b[i] = 0;
  }
  return b;
}

/** Filtruje klasyfikację do maski binarnej dla danej klasy. */
function filterClass(classified: Uint8Array, cls: number): Uint8Array {
  const b = new Uint8Array(classified.length);
  for (let i = 0; i < classified.length; i++) b[i] = classified[i] === cls ? 1 : 0;
  return b;
}

// ── 4-connected BFS labelling ─────────────────────────────────

function labelComponents(
  b: Uint8Array, w: number, h: number,
): { labels: Uint32Array; count: number } {
  const labels = new Uint32Array(w * h);
  let count = 0;
  const q: number[] = [];

  for (let i = 0; i < w * h; i++) {
    if (!b[i] || labels[i]) continue;
    ++count;
    q.length = 0; q.push(i); labels[i] = count;
    for (let head = 0; head < q.length; head++) {
      const cur = q[head];
      const x = cur % w, y = (cur / w) | 0;
      for (let d = 0; d < 4; d++) {
        const nx = x + (d === 0 ? 1 : d === 1 ? -1 : 0);
        const ny = y + (d === 2 ? 1 : d === 3 ? -1 : 0);
        if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
        const ni = ny * w + nx;
        if (b[ni] && !labels[ni]) { labels[ni] = count; q.push(ni); }
      }
    }
  }
  return { labels, count };
}

// First pixel (top-to-bottom, left-to-right) for each component
function componentStarts(
  labels: Uint32Array, count: number, w: number,
): { x: number; y: number }[] {
  const starts = Array.from({ length: count }, () => ({ x: -1, y: -1 }));
  for (let i = 0; i < labels.length; i++) {
    const c = labels[i] - 1;
    if (c < 0 || starts[c].x >= 0) continue;
    starts[c] = { x: i % w, y: (i / w) | 0 };
  }
  return starts;
}

// ── Moore neighbor boundary trace (8-connectivity) ───────────
// Directions: E=0 SE=1 S=2 SW=3 W=4 NW=5 N=6 NE=7
const BDX = [ 1,  1,  0, -1, -1, -1,  0,  1];
const BDY = [ 0,  1,  1,  1,  0, -1, -1, -1];

function traceBoundary(
  b: Uint8Array, w: number, h: number,
  sx: number, sy: number,
): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];

  let cx = sx, cy = sy;
  // Starting pixel is the topmost-leftmost foreground pixel,
  // so its western neighbour is always background → backDir = W (4).
  let backDir = 4;

  // Upper bound: 4 × perimeter of image is more than enough for any shape.
  const limit = 4 * (w + h) + 8;

  do {
    pts.push({ x: cx, y: cy });

    // Scan CW from (backDir + 1) % 8 until we find a foreground neighbour.
    let moved = false;
    for (let i = 1; i <= 8; i++) {
      const d  = (backDir + i) % 8;
      const nx = cx + BDX[d], ny = cy + BDY[d];
      if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
      if (b[ny * w + nx]) {
        backDir = (d + 4) % 8;   // opposite = direction we came from
        cx = nx; cy = ny;
        moved = true;
        break;
      }
    }

    if (!moved || pts.length >= limit) break;
  } while (cx !== sx || cy !== sy);

  return pts;
}

// ── Douglas-Peucker ───────────────────────────────────────────

function dp(
  pts: { x: number; y: number }[],
  eps: number,
): { x: number; y: number }[] {
  if (pts.length <= 2) return pts;
  const { x: sx, y: sy } = pts[0];
  const { x: ex, y: ey } = pts[pts.length - 1];
  const dx = ex - sx, dy = ey - sy;
  const len = Math.sqrt(dx * dx + dy * dy);
  let maxD = 0, maxI = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    const d = len === 0
      ? Math.sqrt((pts[i].x - sx) ** 2 + (pts[i].y - sy) ** 2)
      : Math.abs(dy * pts[i].x - dx * pts[i].y + ex * sy - ey * sx) / len;
    if (d > maxD) { maxD = d; maxI = i; }
  }
  if (maxD > eps) {
    const l = dp(pts.slice(0, maxI + 1), eps);
    const r = dp(pts.slice(maxI), eps);
    return [...l.slice(0, -1), ...r];
  }
  return [pts[0], pts[pts.length - 1]];
}

function arcLen(pts: { x: number; y: number }[]): number {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    s += Math.sqrt((pts[j].x - pts[i].x) ** 2 + (pts[j].y - pts[i].y) ** 2);
  }
  return s;
}

function signedArea(pts: { x: number; y: number }[]): number {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    a += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  return a / 2;
}

// ── Public API ────────────────────────────────────────────────

/**
 * Zwraca zbiór numerów etykiet komponentów dotykających brzegu obrazu.
 * Komponenty z tych etykiet mają zewnętrzną granicę na krawędzi obrazu,
 * co pokrywa się z zewnętrznym obwodem PSLG (outer boundary) → należy je pominąć.
 */
function borderTouchingLabels(labels: Uint32Array, w: number, h: number): Set<number> {
  const s = new Set<number>();
  for (let x = 0; x < w; x++) {
    const t = labels[x];           if (t) s.add(t);
    const b = labels[(h - 1) * w + x]; if (b) s.add(b);
  }
  for (let y = 0; y < h; y++) {
    const l = labels[y * w];           if (l) s.add(l);
    const r = labels[y * w + (w - 1)]; if (r) s.add(r);
  }
  return s;
}

/**
 * Ekstrahuje kontury z maski binarnej.
 * filterBorder=true → pomija komponenty dotykające brzegu obrazu
 * (ich zewnętrzna granica pokryłaby się z PSLG outer boundary → crash).
 */
export function extractContours(
  binary: Uint8Array,
  w: number, h: number,
  scaleX: number, scaleY: number,
  ocx: number, ocy: number,
  filterBorder = false,
): Contour[] {
  const { labels, count } = labelComponents(binary, w, h);
  const skip   = filterBorder ? borderTouchingLabels(labels, w, h) : new Set<number>();
  const starts = componentStarts(labels, count, w);
  const contours: Contour[] = [];

  for (let ci = 0; ci < count; ci++) {
    if (skip.has(ci + 1)) continue;

    const { x: sx, y: sy } = starts[ci];
    const raw = traceBoundary(binary, w, h, sx, sy);
    if (raw.length < 3) continue;

    const arc = arcLen(raw);
    // Cap obniżony z 2.0 → 1.0 px. Przy MAX_PX=1024 koło r≈200px daje ~31
    // segmentów zamiast ~15 przy poprzednim ustawieniu (512px + cap 2.0px).
    const eps = Math.min(0.005 * arc, 1.0);
    const simp = dp([...raw, raw[0]], eps);

    const mmPts: ContourPoint[] = simp.map(p => ({
      x:  (p.x - ocx) * scaleX,
      y: -(p.y - ocy) * scaleY,
    }));

    const a = Math.abs(signedArea(mmPts));
    if (a < MIN_AREA_MM2) continue;

    contours.push({ points: mmPts, area: a, isHole: signedArea(mmPts) > 0 });
  }

  contours.sort((a, b) => b.area - a.area);
  return contours;
}

export async function vectorizeTemplate(
  dataUrl:  string,
  widthMm:  number,
  heightMm: number,
): Promise<VectorizeResult> {
  const { pixels, w, h } = await getPixels(dataUrl, MAX_PX);
  const classified = classifyPixels(pixels, w * h);

  // ── Trójkolorowy szablon: preprocessing ─────────────────────
  const hasRed = classified.some(v => v === 2);

  if (hasRed) {
    // 1. Przezroczyste piksele (poza obrazem) → czerwony (obszar do wycięcia,
    //    nie czarny materiał), aby tło poza szablonem było poprawnie sklasyfikowane.
    for (let i = 0; i < w * h; i++) {
      if (pixels[i * 4 + 3] < 128) classified[i] = 2;
    }

    // 2. Czerwona ramka szerokości B wokół obrazu.
    //
    // Dlaczego B=6 (nie 1)?
    //
    // Problem z małym B: PNG szablonu może mieć artefakt – cienką ciemną
    // „ramkę" przy krawędzi obrazu (np. z Photoshopa, antyaliasingu lub
    // downscalingu). Przed poprawką R≥150 te piksele mogły być klasy 0 lub 1,
    // po poprawce (R≥128 dla czerwonego) piksele z R≥128 i G/B<100 stają się
    // klasą 2, ale czysto ciemne (R<128) nadal są klasą 0.
    // Efekt przy małym B (np. 1 lub 3): czarna ramka na wierszach 3-5 tworzy
    // OSOBNĄ zamkniętą komponentę w combinedMask, oddzieloną czerwienią od
    // właściwego kształtu. filterBorder=true jej NIE odfiltruje, bo nie dotyka
    // wiersza 0. Ta fałszywa komponenta → prostokątny kontur w cutContoursInverted
    // → ultra-gęsta siatka MES przy granicy + cutLabel=0 dla trójkątów między
    // prostokątem a właściwym konturem (zamiast cutLabel=1) → widoczna ramka 3D.
    // B=6 nadpisuje wiersze 0-5 czerwienią (≈4 mm dla 512 px → 350 mm),
    // całkowicie usuwając strefę artefaktów.
    // B skaluje się z rozdzielczością: przy 512 px = 6 px ≈ 4 mm dla arkusza 350 mm.
    // Przy 1024 px ta sama strefa fizyczna = 12 px.
    const B = Math.round(6 * Math.max(w, h) / 512);
    for (let b = 0; b < B; b++) {
      for (let x = 0; x < w; x++) {
        classified[b * w + x]           = 2;
        classified[(h - 1 - b) * w + x] = 2;
      }
      for (let y = 0; y < h; y++) {
        classified[y * w + b]           = 2;
        classified[y * w + (w - 1 - b)] = 2;
      }
    }
  }

  const scaleX = widthMm  / w;
  const scaleY = heightMm / h;
  const ocx    = w / 2;
  const ocy    = h / 2;

  // ── Otwory szablonu (białe piksele) ──────────────────────────
  const whiteMask = filterClass(classified, 1);
  const contours  = extractContours(whiteMask, w, h, scaleX, scaleY, ocx, ocy);

  // ── Obszary do wycięcia (dwa podejścia) ──────────────────────
  //
  // Przypadek A – „standardowy": czerwony obszar jest wewnętrzny (nie dotyka
  //   brzegu obrazu, np. otoczony czarną ramką). Granica zewnętrzna czerwonego
  //   regionu leży wewnątrz PSLG → bezpiecznie jako cutContours.
  //
  // Przypadek B – „czerwony do brzegu": czerwony obszar sięga krawędzi obrazu,
  //   jego granica zewnętrzna = granica materiału → crash Triangle.
  //   Szukamy WEWNĘTRZNYCH WYSP CZARNYCH (tylko klasa 0) otoczonych czerwienią.
  //   Ich zewnętrzna granica = granica czarny/czerwony – to jest właściwa linia cięcia.
  //   Takie kontury trafiają do cutContoursInverted z odwróconą logiką PIP
  //   (poza wyspą = wycięte).

  // DEBUG: zapisz tablicę classified (po ramce) jako PNG na serwer
  await debugSaveClassified(classified, w, h, 'classified');

  // Przypadek A: wewnętrzne komponenty czerwone (nie dotykające brzegu)
  const redMask     = filterClass(classified, 2);
  const cutContours = extractContours(redMask, w, h, scaleX, scaleY, ocx, ocy, true);

  // Przypadek B: wyspy (czarny LUB biały) otoczone czerwienią.
  //
  // UWAGA na błąd, który był tu wcześniej: używaliśmy tylko blackMask (klasa 0).
  // Problem: Moore trace startuje od najwyższego-lewego piksela czarnego.
  // Jeśli ten piksel leży na WEWNĘTRZNEJ granicy pierścienia (czarny dotyka białego
  // od góry, np. przy ogonie kota), algorytm obrysowuje wewnętrzną granicę
  // czarny–biały (zarys otworu), a nie zewnętrzną czarny–czerwony.
  // Wtedy PIP klasyfikuje czarne trójkąty jako "poza wyspą" → cutLabel=1 → błędne wycięcie.
  //
  // Poprawka: łączymy czarne (0) i białe (1) piksele w jedną maskę.
  // Granica wewnętrzna między czarnym a białym ZNIKA (oba są foreground).
  // Zostaje TYLKO zewnętrzna granica (czarny/biały)–czerwony — właściwa linia cięcia.
  // Trójkąty czarne i białe (opening) są "wewnątrz wyspy" → cutLabel=0 ✓
  const combinedMask = new Uint8Array(classified.length);
  for (let i = 0; i < classified.length; i++) {
    combinedMask[i] = (classified[i] === 0 || classified[i] === 1) ? 1 : 0;
  }

  // DEBUG: zapisz combinedMask (biały=foreground dla przypadku B) na serwer
  await debugSaveMask(combinedMask, w, h, 'combinedMask');

  const cutContoursInverted = extractContours(
    combinedMask, w, h, scaleX, scaleY, ocx, ocy, true,
  );

  const hw = widthMm / 2, hh = heightMm / 2;
  const outerBoundary: ContourPoint[] = [
    { x: -hw, y: -hh }, { x: hw, y: -hh },
    { x:  hw, y:  hh }, { x: -hw, y:  hh },
  ];

  return {
    imageSize: { widthPx: w, heightPx: h },
    contours,
    cutContours,
    cutContoursInverted,
    outerBoundary,
  };
}
